import React from 'react';
import { Show, useTable, EditButton, ShowButton, DeleteButton } from '@refinedev/antd';
import { useShow, useOne } from '@refinedev/core';
import { Table, Space, Tag, Button, Divider, theme } from 'antd';
import { PlusOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { AdminSurveyScreenDetailDto, AdminSurveyQuestionListItemDto } from '@wellverse/shared-ts';
import { Field } from '../../components/Field';

export const SurveyScreenShow: React.FC = () => {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const { queryResult } = useShow<AdminSurveyScreenDetailDto>({ resource: 'survey-screens' });
  const item = queryResult?.data?.data;

  const { data: surveyData } = useOne({
    resource: 'surveys',
    id: item?.surveyId ?? '',
    queryOptions: {
      enabled: !!item?.surveyId,
    },
  });
  const survey = (surveyData as any)?.data;

  const { tableProps } = useTable<AdminSurveyQuestionListItemDto>({
    resource: 'survey-questions',
    filters: {
      permanent: item?.id
        ? [
            {
              field: 'screenId',
              operator: 'eq',
              value: item.id,
            },
          ]
        : [],
    },
    queryOptions: {
      enabled: !!item?.id,
    },
  });

  if (!item) {
    return <div style={{ color: token.colorTextSecondary }}>Loading...</div>;
  }

  return (
    <Show resource="survey-screens">
      {survey && (
        <div style={{ marginBottom: 16 }}>
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(`/surveys/show/${survey.id}`)}
            style={{ padding: 0, color: '#3ecf8e' }}
          >
            {survey.name}
          </Button>
        </div>
      )}

      <div style={{ padding: '8px 0' }}>
        <Field label="Screen Key">{item.screenKey}</Field>
        <Field label="Type">{item.type}</Field>
        <Field label="Order Index">{item.orderIndex}</Field>
        <Field label="Metadata">
          <pre style={{ background: token.colorBgContainer, padding: 12, borderRadius: 8 }}>
            {item.metadata ? JSON.stringify(item.metadata, null, 2) : '—'}
          </pre>
        </Field>
      </div>

      <Divider style={{ borderColor: token.colorBorder }} />

      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <h3 style={{ color: token.colorText, margin: 0 }}>Questions</h3>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate(`/survey-questions/create?screenId=${item.id}`)}
        >
          Add Question
        </Button>
      </div>

      <Table {...tableProps} rowKey="id">
        <Table.Column dataIndex="questionKey" title="Question Key" />
        <Table.Column dataIndex="questionText" title="Question Text" />
        <Table.Column dataIndex="type" title="Type" />
        <Table.Column dataIndex="orderIndex" title="Order" />
        <Table.Column
          dataIndex="isRequired"
          title="Required"
          render={(isRequired: boolean) => (
            <Tag
              style={{
                borderColor: isRequired ? '#3ecf8e' : '#898989',
                color: isRequired ? '#3ecf8e' : '#898989',
                background: isRequired ? '#3ecf8e15' : '#89898915',
                borderRadius: 9999,
                padding: '2px 12px',
                fontWeight: 500,
              }}
            >
              {isRequired ? 'Required' : 'Optional'}
            </Tag>
          )}
        />
        <Table.Column dataIndex="optionCount" title="Options" />
        <Table.Column
          title="Actions"
          dataIndex="actions"
          render={(_, record: AdminSurveyQuestionListItemDto) => (
            <Space>
              <ShowButton hideText size="small" recordItemId={record.id} resource="survey-questions" />
              <EditButton hideText size="small" recordItemId={record.id} resource="survey-questions" />
              <DeleteButton hideText size="small" recordItemId={record.id} resource="survey-questions" />
            </Space>
          )}
        />
      </Table>
    </Show>
  );
};
